import { Card as FcCard } from '../ui';
// src/components/CompetitionCard.jsx
// Unified competition card used on the Competitions browse page.
// Shows a coloured league-branded banner, stats row, optional progress bar,
// and a primary action CTA (Register or View Competition).

import { Link } from 'react-router-dom';
import CompetitionCardHeader from './CompetitionCardHeader';
import { getCompetitionBrandAssets, getCompetitionRegistration } from '../lib/competitionUtils';
import { Button as FcButton, LinkButton as FcLinkButton } from '../ui';

// ─── Inline SVG icons ───────────────────────────────────────────────────────
function TrophyIcon({ size = 14, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
      <path d="M6 9H4a2 2 0 0 1-2-2V5h4"/>
      <path d="M18 9h2a2 2 0 0 0 2-2V5h-4"/>
      <path d="M12 17c-3.31 0-6-2.69-6-6V3h12v8c0 3.31-2.69 6-6 6z"/>
      <path d="M12 17v4"/>
      <path d="M9 21h6"/>
    </svg>
  );
}
function PeopleIcon({ size = 14, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  );
}
function ClockIcon({ size = 14, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
      <circle cx="12" cy="12" r="10"/>
      <polyline points="12 6 12 12 16 14"/>
    </svg>
  );
}
function ChevronRightIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
      <polyline points="9 18 15 12 9 6"/>
    </svg>
  );
}
function LockIcon({ size = 12 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
      <rect x="3" y="11" width="18" height="10" rx="2"/>
      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────
/**
 * @param {object}   comp          – competition record (from DB)
 * @param {boolean}  joined        – whether the current user has joined
 * @param {boolean}  canRegister   – registration is currently open
 * @param {function} onJoin        – called with (comp) when Register is clicked
 * @param {function} getRoute      – (id, type) → route string
 */
export default function CompetitionCard({
  comp,
  joined,
  canRegister,
  onJoin,
  getRoute,
  compact = false,
  forceViewAction = false,
  statusBadgeLabel = "",
  footerSummary = "",
  actionLabel = "",
  hideCountdown = false,
  hideStats = false,
  winnerLabel = "",
  leagueBranding = false,
  inlineActions = false,
  now = Date.now(),
}) {
  const registration = getCompetitionRegistration(comp, now);
  const isPrivate = comp.visibility === 'private';
  const brandAssets = getCompetitionBrandAssets(comp);

  const route = getRoute(comp.id, comp.type);

  // Fill % – only shown when max_players is set
  const fillPercent =
    comp.max_players && comp.player_count != null
      ? Math.min(100, Math.round((comp.player_count / comp.max_players) * 100))
      : null;

  // Progress bar colour: green → amber → red as it fills
  const progressColor =
    fillPercent === null ? '#22C55E'
    : fillPercent >= 90  ? '#EF4444'
    : fillPercent >= 70  ? '#F59E0B'
    : '#22C55E';

  const showRegisterAction = !forceViewAction && !joined && canRegister && registration.canRegister;

  return (
    <FcCard as="div" padding="none" className={`bg-surface-card rounded-xl shadow-sm border border-ui-border overflow-hidden flex flex-col group hover:shadow-md transition-shadow duration-200 ${compact ? 'min-w-[248px] max-w-[248px]' : ''}`}>

      {/* ── Banner (shared header) ── */}
      <CompetitionCardHeader comp={comp} compact={compact} leagueBranding={leagueBranding} />

      {/* ── Content ── */}
      <div className={`flex-1 flex flex-col gap-0 ${compact ? 'px-3 pt-2.5 pb-3' : 'px-4 pt-3 pb-4'}`}>

        {/* Name + status badges */}
        <div className={`flex items-start justify-between gap-2 ${compact ? 'mb-0.5' : 'mb-1'}`}>
          <h3 className={`font-bold leading-snug text-content-primary ${compact ? "text-sm" : "text-sm"}`}>{comp.name}</h3>
          {(comp.is_18_plus || isPrivate || statusBadgeLabel) && (
            <div className={`flex items-center gap-1.5 shrink-0 ${compact ? "mt-0 text-xs" : "mt-0.5 text-xs"}`}>
              {comp.is_18_plus && (
                <span className="rounded bg-red-600 text-white font-bold tracking-wide px-1.5 py-0.5">
                  18+
                </span>
              )}
              {isPrivate && (
                <span role="img" aria-label="Private competition" className="p-1 rounded-full bg-gray-100 text-content-secondary border border-ui-border">
                  <LockIcon size={compact ? 10 : 12} />
                </span>
              )}
              {statusBadgeLabel && (
                <span className="rounded bg-gray-100 text-content-primary border border-ui-border font-bold tracking-wide px-1.5 py-0.5">
                  {statusBadgeLabel}
                </span>
              )}
            </div>
          )}
        </div>

        {/* League row */}
        {!leagueBranding && <div className={`flex items-center gap-1.5 text-content-secondary ${compact ? 'text-xs mb-2' : 'text-sm mb-3'}`}>
          {brandAssets.flagUrl ? (
            <img src={brandAssets.flagUrl} alt={comp.league} className="w-4 h-3 object-contain rounded-sm" />
          ) : (
            <span className="leading-none text-base">🌐</span>
          )}
          <span className="font-medium">{comp.league}</span>
        </div>}

        {/* Stats row */}
        {!hideStats && (
        <div className={`flex flex-wrap items-start ${compact ? 'gap-3 mb-2' : 'gap-4 mb-3'}`}>
          {/* Players */}
          <div className="flex flex-col min-w-0">
            <div className={`flex items-center gap-1 text-content-primary font-semibold ${compact ? 'text-xs' : 'text-sm'}`}>
              <PeopleIcon color="#284239" size={compact ? 12 : 14} />
              <span>{comp.player_count ?? 0}</span>
            </div>
            <span className={`text-content-secondary mt-0.5 ${compact ? "text-xs" : "text-xs"}`}>Players Joined</span>
          </div>

          {!hideCountdown && (
            <div className="flex flex-col min-w-0 ml-auto text-right">
              <div className="flex items-center gap-1 text-sm font-semibold text-content-primary">
                <ClockIcon size={compact ? 12 : 14} />
                <span>Registration {registration.state}</span>
              </div>
              {Number.isFinite(registration.deadline) && <time className="text-sm text-content-secondary mt-0.5" dateTime={new Date(registration.deadline).toISOString()}>
                {registration.state === 'open' ? 'Closes' : 'Closing time'} {new Date(registration.deadline).toLocaleString(undefined,{day:'numeric',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit',timeZoneName:'short'})}
              </time>}
            </div>
          )}
        </div>
        )}

        {/* Progress bar – only if max_players is known */}
        {fillPercent !== null && (
          <div className={`${compact ? 'mb-2' : 'mb-3'}`}>
            <div className="flex justify-between items-center mb-1">
              <div className={`w-full bg-gray-100 rounded-full mr-2 ${compact ? 'h-1.5' : 'h-2'}`}>
                <div
                  className={`${compact ? 'h-1.5' : 'h-2'} rounded-full transition-all duration-300`}
                  style={{ width: `${fillPercent}%`, backgroundColor: progressColor }}
                />
              </div>
              <span
                className={`${compact ? "text-xs" : 'text-xs'} font-bold whitespace-nowrap`}
                style={{ color: progressColor }}
              >
                {fillPercent}% Full
              </span>
            </div>
          </div>
        )}

        {footerSummary && (
          <div className={`text-content-primary ${compact ? "text-xs mb-2" : 'text-xs mb-3'} truncate`}>
            {footerSummary}
          </div>
        )}

        {winnerLabel && (
          <div className={`rounded-lg border border-yellow-300 bg-yellow-50 text-yellow-900 font-bold ${compact ? "px-2.5 py-2 text-xs mb-2" : 'px-3 py-2.5 text-sm mb-3'} ${leagueBranding?'':'truncate'}`}>
            Winner: {winnerLabel}
          </div>
        )}

        {/* CTA button */}
        <div className={inlineActions ? 'fc-card-actions' : `mt-auto ${compact ? 'pt-0.5' : 'pt-1'}`}>
          {showRegisterAction && (
            <FcButton variant="primary" size={inlineActions ? 'compact' : 'standard'} type="submit"
              onClick={() => onJoin && onJoin(comp)}
              className={inlineActions ? '' : 'w-full'}
            >
              Register <ChevronRightIcon size={14} />
            </FcButton>
          )}
          {(!showRegisterAction || inlineActions) && (
            <FcLinkButton variant="secondary" size={inlineActions ? 'compact' : 'standard'}
              to={route}
              className={inlineActions ? '' : 'w-full'}
            >
              {actionLabel || (comp.type === 'world_stage' && joined ? 'Open World Stage' : 'View Competition')}
              <ChevronRightIcon size={14} />
            </FcLinkButton>
          )}
        </div>
      </div>
    </FcCard>
  );
}
